import { Link } from '@tanstack/react-router';
import { m } from 'motion/react';
import { Building2, CheckSquare, Plus, Settings, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { AbsorStaffLockup } from '../../components/Brand';
import { useT } from '../../i18n/provider';
import type { Key } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useAuth } from '../../stores/auth';

import { SidebarBody } from './Sidebar';

/**
 * La barra fuera de una empresa: el espacio de trabajo entero.
 *
 * Aquí sí tiene sentido la lista de empresas, porque elegir una es justo lo que
 * se viene a hacer. Dentro de una empresa esta barra desaparece entera y la
 * sustituye `CompanyNav`.
 */
export function GlobalNav({ pathname, onNavigate }: { pathname: string; onNavigate: () => void }) {
  const t = useT();
  const companies = useAuth((state) => state.user?.companies ?? []);

  const items: { to: string; label: Key; icon: LucideIcon; exact: boolean }[] = [
    { to: '/', label: 'nav.companies', icon: Building2, exact: true },
    { to: '/approvals', label: 'nav.approvals', icon: CheckSquare, exact: false },
    { to: '/notifications', label: 'nav.notifications', icon: Sparkles, exact: false },
    { to: '/settings', label: 'nav.account', icon: Settings, exact: false },
  ];

  return (
    <SidebarBody>
      <div className="shrink-0 px-4 pt-6 pb-0">
        <Link to="/" className="mb-9 flex px-2" onClick={onNavigate}>
          <AbsorStaffLockup className="text-lg text-white" />
        </Link>
      </div>

      <nav className="flex shrink-0 flex-col gap-1 px-4">
        {items.map((item) => {
          const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
          const Icon = item.icon;

          return (
            <Link
              key={item.to}
              to={item.to as '/'}
              onClick={onNavigate}
              // El mismo criterio que el resaltado, para que el enrutador no
              // anuncie «Empresas» como página actual en todas las demás.
              activeOptions={{ exact: item.exact }}
              className={cn(
                'relative flex min-h-11 items-center gap-3 rounded-sm px-3 text-sm font-semibold transition-colors duration-fast',
                active ? 'text-white' : 'text-nav-ink-soft hover:bg-white/6 hover:text-white',
              )}
            >
              {active ? (
                <m.span
                  layoutId="sidebar-active"
                  className="absolute inset-0 rounded-sm bg-nav-soft shadow-[inset_3px_0_var(--color-primary-bright)]"
                  transition={{ type: 'spring', stiffness: 520, damping: 42 }}
                />
              ) : null}
              <span className="relative [&>svg]:size-4.5">
                <Icon />
              </span>
              <span className="relative">{t(item.label)}</span>
            </Link>
          );
        })}
      </nav>

      {companies.length > 0 ? (
        <div className="mt-8 min-h-0 grow overflow-y-auto px-4">
          <p className="px-3 pb-2 text-[0.6rem] font-bold tracking-widest text-nav-ink-soft/70 uppercase">
            {t('nav.companies')}
          </p>

          <div className="flex flex-col gap-0.5">
            {companies.map((company) => {
              const active = pathname.startsWith(`/c/${company.slug}`);

              return (
                <Link
                  key={company.id}
                  to="/c/$companySlug"
                  params={{ companySlug: company.slug }}
                  onClick={onNavigate}
                  className={cn(
                    'flex min-h-10 items-center gap-2.5 rounded-sm px-3 text-[0.82rem] transition-colors duration-fast',
                    active ? 'bg-nav-soft font-semibold text-white' : 'text-nav-ink-soft hover:bg-white/6 hover:text-white',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="brand-gradient grid size-6 shrink-0 place-items-center rounded-xs text-[0.58rem] font-extrabold text-white"
                  >
                    {company.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="truncate">{company.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="grow" />
      )}

      <div className="shrink-0 px-4 pb-4">
        <Link
          to="/companies/new"
          onClick={onNavigate}
          className="mt-4 flex min-h-10 items-center justify-center gap-2 rounded-sm border border-white/12 text-[0.8rem] font-semibold text-nav-ink transition-colors hover:border-white/25 hover:bg-white/6"
        >
          <Plus className="size-4" aria-hidden="true" />
          {t('nav.newCompany')}
        </Link>

        <p className="mt-4 flex items-center gap-2 border-t border-white/8 px-3 pt-4 text-[0.66rem] text-nav-ink-soft/80">
          <span className="size-1.5 rounded-full bg-success shadow-[0_0_0_3px] shadow-success/20" aria-hidden="true" />
          {t('nav.systemStatus')}
        </p>
      </div>
    </SidebarBody>
  );
}
